// What each client sends about its customers, the per-customer summary, segments, proposals from data, "Date primite"
// and CSV imports. An answer never echoes an address; a list shows one masked below the editor role.
import { z } from "zod";
import { IdPageQuery, IsoDateTime, PageQuery, Uuid } from "./common";

export const CUSTOMER_BATCH_MAX = 500;
export const CUSTOMER_CHANNELS = ["email", "sms", "whatsapp"] as const;
export type CustomerChannel = (typeof CUSTOMER_CHANNELS)[number];

export const CUSTOMER_ROLES = ["customer", "provider"] as const;
export const CustomerRole = z.enum(CUSTOMER_ROLES);

/** Why one row of a batch or of an import was not taken. Stable codes; never a value from the row. */
export const CUSTOMER_ITEM_CODES = [
  "item_invalid",
  "duplicate_in_batch",
  "medical_patient_refused",
  "medical_activity_refused",
  "attribute_not_declared",
  "attribute_invalid",
  "zone_looks_like_address",
  "consent_proof_missing",
  "consent_address_missing",
  "contact_erased",
  "item_failed",
] as const;
export type CustomerItemCode = (typeof CUSTOMER_ITEM_CODES)[number];

export const ChannelConsentInput = z.strictObject({
  granted: z.boolean(),
  textVersion: z.string().trim().min(1).max(100).optional(),
  at: IsoDateTime.optional(),
  /** The host's own record of the grant. */
  proofRef: z.string().trim().min(1).max(200).optional(),
  /** E-mail only. The host attests the person proved the mailbox, so no confirmation link is needed. */
  mailboxConfirmed: z.boolean().optional(),
});
export type ChannelConsentInput = z.infer<typeof ChannelConsentInput>;

const RankedKey = z.strictObject({ key: z.string().trim().min(1).max(200), name: z.string().trim().min(1).max(200), count: z.number().int().min(0).max(100_000_000) });

const Count = z.number().int().min(0).max(100_000_000);
const Minor = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);

export const CustomerActivityInput = z.strictObject({
  firstOrderAt: IsoDateTime.optional(),
  lastOrderAt: IsoDateTime.optional(),
  ordersCount: Count.optional(),
  orders30d: Count.optional(),
  orders90d: Count.optional(),
  totalValueMinor: Minor.optional(),
  value90dMinor: Minor.optional(),
  currency: z.string().regex(/^[A-Z]{3}$/).optional(),
  topCategories: z.array(RankedKey).max(5).optional(),
  topProducts: z.array(RankedKey).max(5).optional(),
  preferredChannel: z.string().trim().min(1).max(40).optional(),
  /** Only what the tenant's vertical declares; anything else is refused. */
  attributes: z.record(z.string().max(60), z.union([z.string().max(200), z.number(), z.null()])).optional(),
  /** When the host read these numbers. A paid order event from before this moment is already in them. */
  asOf: IsoDateTime.optional(),
});
export type CustomerActivityInput = z.infer<typeof CustomerActivityInput>;

/** One customer. `version` grows with every change: the same one does nothing, a lower one is ignored. */
export const CustomerBatchItem = z.strictObject({
  externalId: z.string().trim().min(1).max(200),
  version: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  email: z.string().trim().max(320).optional(),
  phone: z.string().trim().max(40).optional(),
  firstName: z.string().trim().max(120).optional(),
  lastName: z.string().trim().max(120).optional(),
  locale: z.string().trim().max(20).optional(),
  country: z.string().trim().regex(/^[A-Za-z]{2}$/).optional(),
  city: z.string().trim().max(120).optional(),
  /** A postal code or an area, never a street address. */
  zone: z.string().trim().max(60).optional(),
  /** Month and day: no year, so no age. */
  birthday: z.strictObject({ month: z.number().int().min(1).max(12), day: z.number().int().min(1).max(31) }).optional(),
  tags: z.array(z.string().trim().min(1).max(60)).max(50).optional(),
  role: CustomerRole.optional(),
  consent: z.strictObject({ email: ChannelConsentInput.optional(), sms: ChannelConsentInput.optional(), whatsapp: ChannelConsentInput.optional() }).optional(),
  activity: CustomerActivityInput.optional(),
  deleted: z.boolean().optional(),
});
export type CustomerBatchItem = z.infer<typeof CustomerBatchItem>;

/** Items are checked one by one, so a bad row is reported rather than failing the other 499. */
export const CustomerBatchBody = z.strictObject({ items: z.array(z.unknown()).min(1).max(CUSTOMER_BATCH_MAX) });
export type CustomerBatchBody = z.infer<typeof CustomerBatchBody>;

/**
 * A person typed by someone on the account. The consent is the same statement a batch makes: what was agreed, when, and
 * where the record of it is kept. Without all three the person is counted and nothing about them is kept.
 */
const CustomerPersonFields = {
  email: CustomerBatchItem.shape.email,
  phone: CustomerBatchItem.shape.phone,
  firstName: CustomerBatchItem.shape.firstName,
  lastName: CustomerBatchItem.shape.lastName,
  locale: CustomerBatchItem.shape.locale,
  country: CustomerBatchItem.shape.country,
  city: CustomerBatchItem.shape.city,
  zone: CustomerBatchItem.shape.zone,
  birthday: CustomerBatchItem.shape.birthday,
  tags: CustomerBatchItem.shape.tags,
  role: CustomerBatchItem.shape.role,
  consent: CustomerBatchItem.shape.consent,
  activity: z
    .strictObject({
      firstOrderAt: IsoDateTime.optional(),
      lastOrderAt: IsoDateTime.optional(),
      ordersCount: Count.optional(),
      totalValueMinor: Minor.optional(),
      currency: z.string().regex(/^[A-Z]{3}$/).optional(),
    })
    .optional(),
};
export const CustomerPersonPatch = z.strictObject(CustomerPersonFields);
export type CustomerPersonPatch = z.infer<typeof CustomerPersonPatch>;
export const CustomerPersonCreate = z.strictObject(CustomerPersonFields).refine((person) => !!person.email || !!person.phone, { path: ["email"], message: "an email or a phone is required" });
export type CustomerPersonCreate = z.infer<typeof CustomerPersonCreate>;

export const CustomerBatchResult = z.object({
  received: z.number().int(),
  created: z.number().int(),
  updated: z.number().int(),
  unchanged: z.number().int(),
  withoutConsent: z.number().int(),
  withdrawn: z.number().int(),
  erased: z.number().int(),
  /** Rows about someone erased earlier, taken no further. Only a grant given after the erasure makes them a new person. */
  erasedSkipped: z.number().int(),
  invalid: z.array(z.object({ externalId: z.string().nullable(), code: z.string() })),
});
export type CustomerBatchResult = z.infer<typeof CustomerBatchResult>;

// ---------------------------------------------------------------------------
// The customer list
// ---------------------------------------------------------------------------

export const LIFECYCLE_STAGES = ["new", "active", "at_risk", "lapsed"] as const;
export const LifecycleStage = z.enum(LIFECYCLE_STAGES);
export type LifecycleStage = z.infer<typeof LifecycleStage>;

export const REACH_CHANNELS = ["email", "sms", "viber", "whatsapp"] as const;
export const ReachChannel = z.enum(REACH_CHANNELS);
export type ReachChannel = z.infer<typeof ReachChannel>;
export const ReachCounts = z.object({ email: z.number().int(), sms: z.number().int(), viber: z.number().int(), whatsapp: z.number().int() });
export type ReachCounts = z.infer<typeof ReachCounts>;

export const CustomerListQuery = IdPageQuery.extend({
  search: z.string().trim().min(1).max(200).optional(),
  lifecycle: LifecycleStage.optional(),
  channel: ReachChannel.optional(),
  status: z.enum(["active", "blocked_by_host"]).optional(),
});
export type CustomerListQuery = z.infer<typeof CustomerListQuery>;

export const CustomerRowDto = z.object({
  id: Uuid,
  externalId: z.string().nullable(),
  /** Masked below the editor role. */
  email: z.string().nullable(),
  phone: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  status: z.string(),
  city: z.string().nullable(),
  lifecycle: LifecycleStage.nullable(),
  ordersCount: z.number().int(),
  lastOrderAt: IsoDateTime.nullable(),
  totalValueMinor: z.number().int(),
  currency: z.string().nullable(),
  reachable: z.array(ReachChannel),
});
export type CustomerRowDto = z.infer<typeof CustomerRowDto>;
export const CustomerPage = z.object({ items: z.array(CustomerRowDto), nextCursor: Uuid.nullable() });
export type CustomerPage = z.infer<typeof CustomerPage>;

export const ConsentHistoryDto = z.object({
  id: Uuid,
  family: z.string(),
  source: z.string(),
  textVersion: z.string(),
  proofAt: IsoDateTime,
  proofRef: z.string().nullable(),
  doubleOptIn: z.string(),
  confirmedAt: IsoDateTime.nullable(),
  withdrawnAt: IsoDateTime.nullable(),
  withdrawnSource: z.string().nullable(),
});

export const CustomerCardDto = CustomerRowDto.extend({
  locale: z.string().nullable(),
  country: z.string().nullable(),
  zone: z.string().nullable(),
  birthday: z.object({ month: z.number().int(), day: z.number().int() }).nullable(),
  tags: z.array(z.string()),
  role: z.string().nullable(),
  firstOrderAt: IsoDateTime.nullable(),
  orders30d: z.number().int(),
  orders90d: z.number().int(),
  value90dMinor: z.number().int(),
  topCategories: z.array(z.object({ key: z.string(), name: z.string(), count: z.number().int() })),
  topProducts: z.array(z.object({ key: z.string(), name: z.string(), count: z.number().int() })),
  preferredChannel: z.string().nullable(),
  attributes: z.record(z.string(), z.unknown()),
  consents: z.array(ConsentHistoryDto),
  suppressed: z.object({ email: z.string().nullable(), phone: z.string().nullable() }),
});
export type CustomerCardDto = z.infer<typeof CustomerCardDto>;

export const CUSTOMER_SAVE_OUTCOMES = ["created", "updated", "counted"] as const;
export const CustomerSaveResult = z.object({
  /** `counted`: no proven yes, so nothing about them was kept. */
  outcome: z.enum(CUSTOMER_SAVE_OUTCOMES),
  customer: CustomerCardDto.nullable(),
  /** What was left out and why, as codes: a yes without proof, say. */
  notes: z.array(z.string()),
});
export type CustomerSaveResult = z.infer<typeof CustomerSaveResult>;

// ---------------------------------------------------------------------------
// Segments
// ---------------------------------------------------------------------------

export const SEGMENT_NUMBER_FIELDS = ["daysSinceLastOrder", "daysSinceFirstOrder", "ordersCount", "orders30d", "orders90d", "totalValueMinor", "value90dMinor", "leadScore"] as const;
export const SEGMENT_TEXT_FIELDS = ["city", "zone", "country", "locale", "leadSource", "leadStage", "preferredChannel"] as const;
export const SEGMENT_KEY_FIELDS = ["topCategory", "topProduct"] as const;
export const SEGMENT_FIELDS = [...SEGMENT_NUMBER_FIELDS, ...SEGMENT_TEXT_FIELDS, ...SEGMENT_KEY_FIELDS, "tags", "consentChannel", "lifecycle", "valueTier", "birthdayMonth", "leadOwner"] as const;
export type SegmentFieldName = (typeof SEGMENT_FIELDS)[number];

export const NUMBER_OPS = ["gt", "gte", "lt", "lte", "eq", "between"] as const;
export const TEXT_OPS = ["eq", "neq", "in", "not_in", "starts_with"] as const;
export const DATE_OPS = ["within_next_days", "within_last_days", "older_than_days"] as const;
export const SEGMENT_OPS = [...NUMBER_OPS, "neq", "in", "not_in", "starts_with", "has", "has_any", "has_none", "is_empty", "is_set", ...DATE_OPS] as const;
export type SegmentOp = (typeof SEGMENT_OPS)[number];

/** `attr.<key>`: a vertical attribute. Whether the key exists and what type it has is checked against the tenant's vertical. */
export const SEGMENT_ATTRIBUTE_FIELD = /^attr\.[a-zA-Z][a-zA-Z0-9]{0,59}$/;

const Text = z.string().trim().min(1).max(200);
const TextList = z.array(Text).min(1).max(100);

const numberValue = (op: string, value: unknown): boolean =>
  op === "between" ? z.tuple([z.number(), z.number()]).safeParse(value).success && (value as number[])[0]! <= (value as number[])[1]! : z.number().finite().safeParse(value).success;

const textValue = (op: string, value: unknown): boolean => ((op === "in" || op === "not_in") ? TextList.safeParse(value).success : Text.safeParse(value).success);

function fieldAccepts(field: string, op: string, value: unknown): string | null {
  const opsOf = (ops: readonly string[]) => (ops.includes(op) ? null : "op_not_allowed");
  if ((SEGMENT_NUMBER_FIELDS as readonly string[]).includes(field)) return opsOf(NUMBER_OPS) ?? (numberValue(op, value) ? null : "value_invalid");
  if ((SEGMENT_TEXT_FIELDS as readonly string[]).includes(field)) {
    if (op === "starts_with" && field !== "zone") return "op_not_allowed";
    return opsOf(TEXT_OPS) ?? (textValue(op, value) ? null : "value_invalid");
  }
  if ((SEGMENT_KEY_FIELDS as readonly string[]).includes(field)) return opsOf(["eq", "in"]) ?? (textValue(op, value) ? null : "value_invalid");
  switch (field) {
    case "tags":
      return opsOf(["has", "has_any", "has_none"]) ?? ((op === "has" ? Text : TextList).safeParse(value).success ? null : "value_invalid");
    case "consentChannel":
      return opsOf(["eq", "in"]) ?? ((op === "eq" ? ReachChannel : z.array(ReachChannel).min(1)).safeParse(value).success ? null : "value_invalid");
    case "lifecycle":
      return opsOf(["eq", "in"]) ?? ((op === "eq" ? LifecycleStage : z.array(LifecycleStage).min(1)).safeParse(value).success ? null : "value_invalid");
    case "valueTier":
      return opsOf(["eq"]) ?? (z.enum(["top10", "rest"]).safeParse(value).success ? null : "value_invalid");
    case "birthdayMonth":
      return opsOf(["eq"]) ?? (z.union([z.literal("current"), z.number().int().min(1).max(12)]).safeParse(value).success ? null : "value_invalid");
    case "leadOwner":
      return opsOf(["is_empty", "is_set"]) ?? (value === undefined || value === null ? null : "value_invalid");
    default:
      break;
  }
  if (!SEGMENT_ATTRIBUTE_FIELD.test(field)) return "field_unknown";
  if ((DATE_OPS as readonly string[]).includes(op)) return z.number().int().min(0).max(36500).safeParse(value).success ? null : "value_invalid";
  if ((NUMBER_OPS as readonly string[]).includes(op) && op !== "eq") return numberValue(op, value) ? null : "value_invalid";
  if (op === "eq" || op === "neq" || op === "in" || op === "not_in") return op === "eq" && typeof value === "number" ? null : textValue(op, value) ? null : "value_invalid";
  return "op_not_allowed";
}

export const SegmentCondition = z
  .strictObject({ field: z.string().max(80), op: z.enum(SEGMENT_OPS), value: z.unknown().optional() })
  .superRefine((condition, ctx) => {
    const problem = fieldAccepts(condition.field, condition.op, condition.value);
    if (problem) ctx.addIssue({ code: "custom", path: [problem === "field_unknown" ? "field" : problem === "op_not_allowed" ? "op" : "value"], message: problem });
  });
export type SegmentCondition = z.infer<typeof SegmentCondition>;

/** `all` must each hold, and when `any` is there at least one of it must too. */
export const SegmentRules = z
  .strictObject({ all: z.array(SegmentCondition).max(30).optional(), any: z.array(SegmentCondition).max(30).optional() })
  .refine((rules) => (rules.all?.length ?? 0) + (rules.any?.length ?? 0) > 0, { message: "at least one condition" });
export type SegmentRules = z.infer<typeof SegmentRules>;

export const SegmentCreate = z.strictObject({ name: z.string().trim().min(1).max(120), rules: SegmentRules, presetId: z.string().max(80).optional() });
export type SegmentCreate = z.infer<typeof SegmentCreate>;
export const SegmentPatch = z.strictObject({ name: z.string().trim().min(1).max(120).optional(), rules: SegmentRules.optional() });
export type SegmentPatch = z.infer<typeof SegmentPatch>;

export const SegmentDto = z.object({ id: Uuid, name: z.string(), rules: SegmentRules, presetId: z.string().nullable(), createdByName: z.string().nullable(), createdAt: IsoDateTime, updatedAt: IsoDateTime });
export type SegmentDto = z.infer<typeof SegmentDto>;
export const SegmentPage = z.object({ items: z.array(SegmentDto), nextCursor: Uuid.nullable() });
export type SegmentPage = z.infer<typeof SegmentPage>;

export const SegmentCountDto = z.object({
  total: z.number().int(),
  reachable: ReachCounts,
  /** People no channel can reach. Someone on SMS alone is reachable, even if email misses them. */
  unreachable: z.number().int(),
  /** Owner and editor only: empty for anyone else. */
  sample: z.array(z.object({ id: Uuid, name: z.string().nullable(), email: z.string().nullable(), phone: z.string().nullable() })),
});
export type SegmentCountDto = z.infer<typeof SegmentCountDto>;

export const SegmentPresetDto = z.object({
  id: z.string(),
  name: z.object({ ro: z.string(), bg: z.string(), hu: z.string(), en: z.string() }),
  rules: SegmentRules.nullable(),
  /** The one choice left to the person: a category, a make, a stage. Its value fills the rule. */
  parameter: z.object({ field: z.string(), op: z.string() }).nullable(),
});
export type SegmentPresetDto = z.infer<typeof SegmentPresetDto>;
export const SegmentOptionsDto = z.object({
  attributes: z.array(z.object({ key: z.string(), type: z.string() })),
  categories: z.array(z.object({ key: z.string(), name: z.string() })),
  products: z.array(z.object({ key: z.string(), name: z.string() })),
  cities: z.array(z.string()),
});
export type SegmentOptionsDto = z.infer<typeof SegmentOptionsDto>;
export const SegmentPresetList = z.object({ items: z.array(SegmentPresetDto) });

// ---------------------------------------------------------------------------
// Proposals from data
// ---------------------------------------------------------------------------

export const PROPOSAL_KINDS = ["win_back", "weak_day", "idle_product", "growing_product", "empty_hours", "new_customers", "best_customers"] as const;
export const ProposalKind = z.enum(PROPOSAL_KINDS);
export type ProposalKind = z.infer<typeof ProposalKind>;
export const PROPOSAL_STATUSES = ["open", "accepting", "accepted", "dismissed", "expired"] as const;
export const ProposalStatus = z.enum(PROPOSAL_STATUSES);
export type ProposalStatus = z.infer<typeof ProposalStatus>;

const FourTexts = z.object({ ro: z.string(), bg: z.string(), hu: z.string(), en: z.string() });

export const ProposalDto = z.object({
  id: Uuid,
  kind: ProposalKind,
  status: ProposalStatus,
  /** Numbers read from the data, and the same reason written out. */
  reason: z.object({ code: z.string(), params: z.record(z.string(), z.unknown()), texts: FourTexts }),
  audience: z.object({ rules: SegmentRules.nullable(), total: z.number().int().nullable(), reachable: ReachCounts.nullable() }),
  channels: z.array(z.string()),
  subjectKey: z.string().nullable(),
  suggestedBudget: z.object({ dailyMinor: z.number().int(), currency: z.string(), basis: z.string() }).nullable(),
  schedule: z.array(z.object({ days: z.array(z.number().int()), startHour: z.number().int(), endHour: z.number().int() })).nullable(),
  draftKind: z.string().nullable(),
  draftRef: z.string().nullable(),
  errorCode: z.string().nullable(),
  decidedByName: z.string().nullable(),
  decidedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type ProposalDto = z.infer<typeof ProposalDto>;
export const ProposalPage = z.object({ items: z.array(ProposalDto), nextCursor: Uuid.nullable() });
export type ProposalPage = z.infer<typeof ProposalPage>;
export const ProposalListQuery = IdPageQuery.extend({ status: ProposalStatus.optional() });
export type ProposalListQuery = z.infer<typeof ProposalListQuery>;
export const ProposalAccept = z.strictObject({ connectionId: Uuid.optional(), language: z.enum(["ro", "bg", "hu", "en"]).optional() });
export type ProposalAccept = z.infer<typeof ProposalAccept>;

// ---------------------------------------------------------------------------
// Date primite
// ---------------------------------------------------------------------------

export const FEED_FRESHNESS = ["fresh", "stale", "missing", "no_rule"] as const;
export const DataFeedRow = z.object({
  kind: z.string(),
  source: z.string().nullable(),
  lastReceivedAt: IsoDateTime.nullable(),
  items: z.number().int(),
  version: z.number().nullable(),
  lastErrorCode: z.string().nullable(),
  lastErrorAt: IsoDateTime.nullable(),
  freshness: z.enum(FEED_FRESHNESS),
  staleAfterDays: z.number().int().nullable(),
  /** What it costs when this kind is missing or stale. Null when nothing is wrong. */
  cost: z.object({ code: z.string(), texts: FourTexts }).nullable(),
});
export type DataFeedRow = z.infer<typeof DataFeedRow>;

export const CustomersFeedDto = z.object({
  source: z.string().nullable(),
  received: z.number().int(),
  withConsent: z.object({ email: z.number().int(), phone: z.number().int() }),
  withoutConsent: z.number().int(),
  withdrawn: z.number().int(),
  erased: z.number().int(),
  lastBatchAt: IsoDateTime.nullable(),
  lastBatch: z.record(z.string(), z.number()).nullable(),
  lastErrorCode: z.string().nullable(),
  freshness: z.enum(FEED_FRESHNESS),
  cost: z.object({ code: z.string(), texts: FourTexts }).nullable(),
});

export const EventTally = z.object({ type: z.string(), last24h: z.number().int(), last7d: z.number().int() });
export const DataSourcesDto = z.object({
  feeds: z.array(DataFeedRow),
  customers: CustomersFeedDto,
  events: z.object({ types: z.array(EventTally), rejected: z.array(z.object({ code: z.string(), last24h: z.number().int(), last7d: z.number().int() })) }),
});
export type DataSourcesDto = z.infer<typeof DataSourcesDto>;

export const HostDataSourcesQuery = PageQuery.extend({ cursor: Uuid.optional() });
export const HostDataSourcesPage = z.object({
  items: z.array(z.object({ tenantId: Uuid, hostRef: z.string().nullable(), name: z.string(), vertical: z.string(), sources: DataSourcesDto })),
  nextCursor: Uuid.nullable(),
});
export type HostDataSourcesPage = z.infer<typeof HostDataSourcesPage>;

// ---------------------------------------------------------------------------
// CSV imports
// ---------------------------------------------------------------------------

export const IMPORT_KINDS = ["customers", "products", "services", "sales", "leads"] as const;
export const ImportKind = z.enum(IMPORT_KINDS);
export type ImportKind = z.infer<typeof ImportKind>;
export const IMPORT_MAX_BYTES = 10 * 1024 * 1024;
export const IMPORT_STATUSES = ["uploaded", "mapped", "applying", "applied", "failed", "expired"] as const;

export const CUSTOMER_IMPORT_FIELDS = ["externalId", "email", "phone", "firstName", "lastName", "locale", "country", "city", "zone", "tags", "role", "consentEmail", "consentSms", "consentWhatsapp", "consentAt", "birthMonth", "birthDay", "lastOrderAt", "firstOrderAt", "ordersCount", "totalValue", "currency"] as const;
export const SERVICE_IMPORT_FIELDS = ["key", "name", "category", "description", "price", "priceFrom", "priceTo", "currency", "durationMinutes", "available"] as const;
export const LEAD_IMPORT_FIELDS = ["email", "phone", "firstName", "lastName", "company", "city", "role", "consent"] as const;
export const PRODUCT_IMPORT_FIELDS = ["key", "name", "category", "description", "price", "currency", "available", "photoUrl"] as const;
export const SALES_IMPORT_FIELDS = ["periodFrom", "periodTo", "currency", "productKey", "productName", "quantity", "revenue", "day", "hour", "orders"] as const;

export const ConsentDeclaration = z.strictObject({
  /** Where the consent was gathered: a form on the site, paper at the counter. */
  gatheredWhere: z.string().trim().min(3).max(500),
  gatheredAt: IsoDateTime,
  /** The words the people agreed to. */
  textUsed: z.string().trim().min(3).max(4000),
  textVersion: z.string().trim().min(1).max(100),
});
export type ConsentDeclaration = z.infer<typeof ConsentDeclaration>;

export const ImportMapping = z.strictObject({
  /** Column header in the file to the field it fills. */
  columns: z.record(z.string().max(200), z.string().max(40)),
  declaration: ConsentDeclaration.optional(),
  defaults: z.strictObject({ currency: z.string().regex(/^[A-Z]{3}$/).optional() }).optional(),
});
export type ImportMapping = z.infer<typeof ImportMapping>;

export const ImportRowError = z.object({ row: z.number().int(), code: z.string() });
export const ImportPreviewDto = z.object({
  id: Uuid,
  rows: z.number().int(),
  /** Rows that would be taken with proof of consent, or as items for a feed. */
  accepted: z.number().int(),
  withoutConsent: z.number().int(),
  /** Rows about someone erased earlier, that applying would leave out. */
  erasedSkipped: z.number().int(),
  errors: z.array(ImportRowError),
  errorCount: z.number().int(),
  sample: z.array(z.record(z.string(), z.unknown())),
});
export type ImportPreviewDto = z.infer<typeof ImportPreviewDto>;

export const ImportDto = z.object({
  id: Uuid,
  kind: ImportKind,
  status: z.enum(IMPORT_STATUSES),
  fileName: z.string().nullable(),
  byteSize: z.number().int(),
  rowCount: z.number().int(),
  columns: z.array(z.string()),
  mapping: z.record(z.string(), z.string()).nullable(),
  declaration: ConsentDeclaration.extend({ declaredById: z.string().nullable(), declaredByName: z.string().nullable(), declaredAt: IsoDateTime }).nullable(),
  result: z.record(z.string(), z.unknown()).nullable(),
  errorCode: z.string().nullable(),
  appliedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type ImportDto = z.infer<typeof ImportDto>;
export const ImportPage = z.object({ items: z.array(ImportDto), nextCursor: Uuid.nullable() });
